import {createFeatureSelector, createSelector} from '@ngrx/store';
import {
  generateIndependentSelectorCreatorsFor,
  generateLoadStatusSelectorsFor,
  generatePersonNameSelectorCreatorsFor,
  generatePersonNameSelectorsFor,
  generateSelectorCreatorsDependentOn,
  generateSelectorsDependentOn
} from '@aviary/store/store.selector-generators';
import {GeneratedSelectorsFor, MxSelector, MxSelectorCreator, NO_NAME_PREFIX} from '@aviary/store/store.types';
import {
  FALLBACK_ACTIVE_HOLD_COUNTS_BY_STATUS,
  FALLBACK_NOTIFICATION_PREFERENCE,
  FALLBACK_PATRON,
  FALLBACK_PATRON_ACCOUNT_STATUS,
  FALLBACK_PATRON_ADDRESS,
  FALLBACK_PATRON_NOTIFICATION_PREFERENCES,
  FALLBACK_PATRON_REFERENCE_OPTIONS,
  FALLBACK_PATRON_RELATIONSHIPS,
  FALLBACK_PRONOUN,
  FALLBACK_RELATED_PATRON
} from './patron.fallback';
import {PatronState} from './patron.state';
import {
  ActiveHoldCountsByStatus,
  EditNotificationPreferencesValue,
  EditPatronValue,
  ExtendedPatronNotificationPreferenceValue,
  NotificationPreference,
  Patron,
  PATRON_FEATURE_KEY,
  PatronAccountStatus,
  PatronAddress,
  PatronNotificationPreferences,
  PatronNotificationPreferenceType,
  PatronReferenceOptions,
  PatronRelationships as PatronRelationshipsData,
  PatronSettingsQuickTogglesValue,
  RelatedPatron
} from './patron.types';
import {convertToSelectOptions} from '@aviary/store/store.helpers';
import {isPopulatedArray, isPopulatedString, isPositiveNumber} from '@aviary/store/common/typing.helpers';
import {alwaysEmptyString, alwaysEmptyStringFor} from '@aviary/store/common/common.selectors';
import {Pronoun, SelectOptions} from '@aviary/store/common/common.types';
import {ACTIVE_HOLD_STATUSES, ON_HOLD_HOLD_STATUSES, READY_FOR_PICKUP_HOLD_STATUSES} from '@store/holds/holds.types';
import {calculateTotalHoldCountsFor} from '@store/patron/patron.helpers';
import {PatronCategoryColor} from '@aviary/store/common/patron.types';
import {calculatePatronCategoryColorFor} from '@aviary/store/common/patron.helpers';

const entireState: MxSelector<PatronState> = createFeatureSelector(PATRON_FEATURE_KEY);

export namespace PatronSelectors {
  export namespace Stateless {
    const independentSelectorCreators = generateIndependentSelectorCreatorsFor(FALLBACK_PATRON, NO_NAME_PREFIX);

    export const {
      idFor,
      googleUidFor,
      branchIdFor,
      patronCategoryIdFor,
      barcodeFor,
      preferredNameFor,
      emailFor,
      notificationEmailFor,
      firstNameFor,
      middleNameFor,
      lastNameFor,
      suffixFor,
      dateOfBirthFor,
      accountExpirationFor,
      phone1For,
      phone1TypeFor,
      phone1MobileFor,
      phone2For,
      phone2MobileFor,
      addressFor,
      address2For,
      cityFor,
      stateFor,
      zipCodeFor,
      emailVerifiedFor,
      optInReadingHistoryFor,
      lookupAttemptsFor,
      underBalanceLimitFor,
      categoryFor,
      addressesFor,
      openHoldsFor,
      openLoansFor,
      accountBalanceFor,
      patronRuleFor,
      patronStatisticsFor,
      pronounsFor,
    } = independentSelectorCreators;

    /*
    export const branchSelectOptions: MxSelector<SelectOptions<number>> = createSelector(
      branches,
      _branches => convertToSelectOptions(_branches, 'name', 'id')
    );
     */

    export const {
      categoryNameFor,
      categoryChipColorFor,
    } = generateSelectorCreatorsDependentOn(categoryFor, FALLBACK_PATRON.category, 'category');

    export const categoryColorFor: MxSelectorCreator<[Patron], PatronCategoryColor> =
      _patron => createSelector(
        categoryChipColorFor(_patron),
        patronCategoryIdFor(_patron),
        calculatePatronCategoryColorFor
      );

    export const editPatronInfoValueFor: MxSelectorCreator<[Patron], EditPatronValue> =
      patron => createSelector({
        id: idFor(patron),
        firstName: firstNameFor(patron),
        lastName: lastNameFor(patron),
        address: addressFor(patron),
        address2: address2For(patron),
        city: cityFor(patron),
        state: stateFor(patron),
        zipCode: zipCodeFor(patron),
        phone1: phone1For(patron),
        phone1Type: phone1TypeFor(patron),
        branchId: branchIdFor(patron),
        dateOfBirth: dateOfBirthFor(patron)
      });
  } // namespace Stateless

  export namespace Auth {
    const subState: MxSelector<PatronState['auth']> = createSelector(
      entireState,
      state => state.auth
    );

    // LOAD STATUS
    export const {
      loading,
      awaitingFirstLoad,
      loaded,
      finishedLoading
    } = generateLoadStatusSelectorsFor(subState);

    // PATRON
    export const patron: MxSelector<Patron> = createSelector(
      subState,
      _subState => _subState.data ?? FALLBACK_PATRON
    );

    export const {
      id,
      email,
      barcode,
      firstName,
      middleName,
      lastName,
      address,
      address2,
      city,
      state,
      zipCode,
      phone1,
      phone1Ext,
      phone1Mobile,
      phone2,
      phone2Ext,
      phone2Mobile,
      phoneCall,
      branchId,
      defaultHoldPickupBranchId,
      optInReadingHistory,
      accountExpiration,
      googleUid,
      suffix,
      preferredName,
      pronouns,
      addresses,
      dateOfBirth,
      oneClickHoldsEnabled,
      notificationEmail
    } = generateSelectorsDependentOn(patron, FALLBACK_PATRON, NO_NAME_PREFIX);

    const {
      pronounsString
    } = generateSelectorsDependentOn(pronouns, FALLBACK_PATRON.pronouns, NO_NAME_PREFIX);

    export const {
      fullName,
      fullPreferredName
    } = generatePersonNameSelectorsFor(firstName, middleName, lastName, suffix, preferredName, pronounsString, NO_NAME_PREFIX);

    export const hasPatron: MxSelector<boolean> = createSelector(
      id,
      isPositiveNumber
    )

    export const hasGoogleUid: MxSelector<boolean> = createSelector(
      googleUid,
      isPopulatedString
    );

    export const pronounId: MxSelector<number> = createSelector(
      pronouns,
      _pronouns => _pronouns.id
    )

    export const primaryAddress: MxSelector<PatronAddress> = createSelector(
      addresses,
      (addresses) => {
        let primaryAddress: PatronAddress = FALLBACK_PATRON_ADDRESS;
        if (isPopulatedArray(addresses)) {
          primaryAddress = addresses.find(address => address.primary) ?? FALLBACK_PATRON_ADDRESS;
        }
        return primaryAddress;
      }
    )

    export const myProfileEditValue: MxSelector<EditPatronValue> = createSelector({
      preferredName,
      phone1,
      phone1Mobile,
      phone1Ext,
      phone2,
      phone2Mobile,
      phone2Ext,
      defaultHoldPickupBranchId,
      pronounId,
      notificationEmail
    });

    export const initials: MxSelector<string> = createSelector(
      firstName,
      lastName,
      (first, last) => `${first.charAt(0)}${last.charAt(0)}`
    )

    export const eligibleSmsNumberOptions: MxSelector<SelectOptions<string>> =
      createSelector(
        phone1, phone1Mobile, phone2, phone2Mobile,
        (phone1, phone1Mobile, phone2, phone2Mobile) => {
          const eligibleNumbers: SelectOptions<string> = [];
          if (isPopulatedString(phone1) && phone1Mobile) {
            eligibleNumbers.push({displayName: phone1, value: phone1})
          }
          if (isPopulatedString(phone2) && phone2Mobile) {
            eligibleNumbers.push({displayName: phone2, value: phone2})
          }

          return eligibleNumbers;
        }
      );

    export const settingsQuickTogglesEditValue: MxSelector<PatronSettingsQuickTogglesValue> = createSelector(
      optInReadingHistory, oneClickHoldsEnabled,
      (optInReadingHistory, oneClickHoldsEnabled) => {
        return {optInReadingHistory, oneClickHoldsEnabled};
      }
    );

    export const isEligibleForRegisterExistingAccount: MxSelector<boolean> = createSelector(
      hasPatron,
      googleUid,
      (hasPatron, googleUid) => hasPatron && !isPopulatedString(googleUid)
    )

    export const cityStateZip: MxSelector<string> = createSelector(
      city,
      state,
      zipCode,
      (_city, _state, _zip) =>
        `${_city}${isPopulatedString(_city) && isPopulatedString(_state) ? ', ' : ''}${_state} ${_zip}`.trim()
    );
  } // namespace Auth

  export namespace AccountStatus {
    const subState: MxSelector<PatronState['patronAccountStatus']> = createSelector(
      entireState,
      state => state.patronAccountStatus
    );

    // LOAD STATUS
    export const {
      loading,
      awaitingFirstLoad,
      loaded,
      finishedLoading
    } = generateLoadStatusSelectorsFor(subState);

    export const status: MxSelector<PatronAccountStatus> = createSelector(
      subState,
      _subState => _subState.data ?? FALLBACK_PATRON_ACCOUNT_STATUS
    );

    const generatedStatusSelectors: GeneratedSelectorsFor<PatronAccountStatus, typeof NO_NAME_PREFIX> =
      generateSelectorsDependentOn(status, FALLBACK_PATRON_ACCOUNT_STATUS, NO_NAME_PREFIX);

    export const {
      blocks,
      currentAccountBalance,
      maximumAccountBalance,
      depositBalance,
      walletBalance,
      unresolvedCheckoutCount,
      unresolvedItemRequestCount,
      underItemRequestLimit,
      monthlyItemRequestLimit
    } = generatedStatusSelectors;

    const {
      activeHoldCountsByStatus
    } = generatedStatusSelectors;

    // The API omits zero-count statuses. This normalizes the API's partial object to one with ALL statuses represented, for safety when totaling.
    const allActiveHoldCountsByStatus: MxSelector<ActiveHoldCountsByStatus> = createSelector(
      activeHoldCountsByStatus,
      counts => ({
        ...FALLBACK_ACTIVE_HOLD_COUNTS_BY_STATUS,
        ...counts
      })
    );

    export const readyForPickupHoldsCount: MxSelector<number> = createSelector(
      allActiveHoldCountsByStatus,
      counts => calculateTotalHoldCountsFor(READY_FOR_PICKUP_HOLD_STATUSES, counts)
    );

    export const onHoldHoldsCount: MxSelector<number> = createSelector(
      allActiveHoldCountsByStatus,
      counts => calculateTotalHoldCountsFor(ON_HOLD_HOLD_STATUSES, counts)
    );

    // The API also supplies this directly, but we ignore it and calculate it ourselves so that we control the meaning of "active holds".
    export const activeHoldsCount: MxSelector<number> = createSelector(
      allActiveHoldCountsByStatus,
      counts => calculateTotalHoldCountsFor(ACTIVE_HOLD_STATUSES, counts)
    );

    export const noBlocks: MxSelector<boolean> = createSelector(
      blocks,
      (_blocks) => _blocks.length === 0
    )

    export const hasBlocks: MxSelector<boolean> = createSelector(
      blocks,
      (_blocks) => isPopulatedArray(_blocks)
    )

    export const hasBalance: MxSelector<boolean> = createSelector(
      currentAccountBalance,
      (currentBalance) => currentBalance > 0
    )

    export const underMaxBalance: MxSelector<boolean> = createSelector(
      currentAccountBalance,
      maximumAccountBalance,
      (currentBalance, maximumBalance) => currentBalance < maximumBalance
    )

    export const isExpired: MxSelector<boolean> = createSelector(
      blocks,
      (_blocks) => _blocks.includes('CARD_EXPIRED')
    )
  } // namespace AccountStatus

  export namespace ReferenceOptions {
    const subState: MxSelector<PatronState['referenceOptions']> = createSelector(
      entireState,
      state => state.referenceOptions
    );

    export const {
      loading,
      loaded
    } = generateLoadStatusSelectorsFor(subState);

    export const options: MxSelector<PatronReferenceOptions> = createSelector(
      subState,
      ({data}) => data ?? FALLBACK_PATRON_REFERENCE_OPTIONS
    );

    export const {
      pronouns,
      branches,
      addressTypes
    } = generateSelectorsDependentOn(options, FALLBACK_PATRON_REFERENCE_OPTIONS, NO_NAME_PREFIX);

    export const pronounSelectOptions: MxSelector<SelectOptions<number>> = createSelector(
      pronouns,
      _pronouns => convertToSelectOptions(_pronouns, 'pronounsString', 'id')
    );

    export const branchSelectOptions: MxSelector<SelectOptions<number>> = createSelector(
      branches,
      _branches => convertToSelectOptions(_branches, 'name', 'id')
    );

    export const pronounById: MxSelectorCreator<[number], Pronoun> = (id: number) => createSelector(
      pronouns,
      _pronouns => _pronouns.find(p => p.id === id) ?? FALLBACK_PRONOUN
    );
  } // namespace ReferenceOptions

  export namespace NotificationPreferences {
    const subState: MxSelector<PatronState['notificationPreferences']> = createSelector(
      entireState,
      state => state.notificationPreferences
    );

    // LOAD STATUS
    export const {
      loading,
      awaitingFirstLoad,
      loaded,
      finishedLoading
    } = generateLoadStatusSelectorsFor(subState);

    export const notificationPreferences: MxSelector<PatronNotificationPreferences> = createSelector(
      subState,
      _subState => _subState.data ?? FALLBACK_PATRON_NOTIFICATION_PREFERENCES
    );

    export const {
      phoneSms,
      notificationEmail,
      notifications,
      smsTermsAccepted
    } = generateSelectorsDependentOn(notificationPreferences, FALLBACK_PATRON_NOTIFICATION_PREFERENCES, NO_NAME_PREFIX);

    export const accountNotifications: MxSelector<ExtendedPatronNotificationPreferenceValue> =
      createSelector(
        notifications,
        preferences => notificationPreferenceToSelection(findNotificationPreferenceFor(preferences, 'ACCOUNT'))
      );

    export const renewNotifications: MxSelector<ExtendedPatronNotificationPreferenceValue> =
      createSelector(
        notifications,
        preferences => notificationPreferenceToSelection(findNotificationPreferenceFor(preferences, 'AUTO_RENEW'))
      );

    export const expirationNotifications: MxSelector<ExtendedPatronNotificationPreferenceValue> =
      createSelector(
        notifications,
        preferences => notificationPreferenceToSelection(findNotificationPreferenceFor(preferences, 'CARD_EXPIRE'))
      );

    export const receiptNotifications: MxSelector<ExtendedPatronNotificationPreferenceValue> =
      createSelector(
        notifications,
        preferences => notificationPreferenceToSelection(findNotificationPreferenceFor(preferences, 'RECEIPT'))
      );

    export const holdNotifications: MxSelector<ExtendedPatronNotificationPreferenceValue> =
      createSelector(
        notifications,
        preferences => notificationPreferenceToSelection(findNotificationPreferenceFor(preferences, 'HOLD'))
      );

    export const noticeNotifications: MxSelector<ExtendedPatronNotificationPreferenceValue> =
      createSelector(
        notifications,
        preferences => notificationPreferenceToSelection(findNotificationPreferenceFor(preferences, 'NOTICE'))
      );

    export const itemRequestNotifications: MxSelector<ExtendedPatronNotificationPreferenceValue> =
      createSelector(
        notifications,
        preferences => notificationPreferenceToSelection(findNotificationPreferenceFor(preferences, 'ITEM_REQUEST'))
      );

    export const notificationPreferencesEditValue: MxSelector<Partial<EditNotificationPreferencesValue>> = createSelector(
      phoneSms, notificationEmail, accountNotifications, renewNotifications, expirationNotifications, receiptNotifications,
      holdNotifications, noticeNotifications, itemRequestNotifications, smsTermsAccepted,
      (phoneSms, notificationEmail, accountNotifications, renewNotifications, expirationNotifications, receiptNotifications,
       holdNotifications, noticeNotifications, itemRequestNotifications, smsTermsAccepted) => {
        return {
          phoneSms,
          notificationEmail,
          accountNotifications,
          renewNotifications,
          expirationNotifications,
          receiptNotifications,
          holdNotifications,
          noticeNotifications,
          itemRequestNotifications,
          smsTermsAccepted
        } as Partial<EditNotificationPreferencesValue>;
      }
    );
  } // namespace NotificationPreferences

  export namespace AddressStateless {
    export const {
      addressFor,
      address2For,
      addressTypeFor,
      stateFor,
      countryFor,
      cityFor,
      primaryFor,
      verifiedFor,
      zipCodeFor
    } = generateIndependentSelectorCreatorsFor(FALLBACK_PATRON_ADDRESS, NO_NAME_PREFIX);

    export const {
      addressTypeDescriptionFor
    } = generateSelectorCreatorsDependentOn(addressTypeFor, FALLBACK_PATRON_ADDRESS.addressType, 'addressType');

    export const addressTypeWithPrimaryLabelFor: MxSelectorCreator<[PatronAddress], string> =
      _address => createSelector(
        addressTypeDescriptionFor(_address),
        primaryFor(_address),
        (type, primary) => `${primary ? 'Primary Address (' : ''}${type}${primary ? ')' : ''}`
      );

    export const addressLine1And2For: MxSelectorCreator<[PatronAddress], string> =
      _address => createSelector(
        addressFor(_address),
        address2For(_address),
        (address1, address2) => `${address1} ${address2}`
      );

    export const cityStateZipFor: MxSelectorCreator<[PatronAddress], string> =
      _address => createSelector(
        cityFor(_address),
        stateFor(_address),
        zipCodeFor(_address),
        (city, state, zip) => `${city}, ${state} ${zip}`
      );
  } // namespace AddressStateless

  export namespace PatronRelationships {
    const subState: MxSelector<PatronState['patronRelationships']> = createSelector(
      entireState,
      state => state.patronRelationships
    );

    // LOAD STATUS
    export const {
      loading,
      awaitingFirstLoad,
      loaded,
      finishedLoading
    } = generateLoadStatusSelectorsFor(subState);

    export const patronRelationships: MxSelector<PatronRelationshipsData> = createSelector(
      subState,
      _subState => _subState.data ?? FALLBACK_PATRON_RELATIONSHIPS
    );

    export const {
      dependents,
      linkedAccounts,
      guardians
    } = generateSelectorsDependentOn(patronRelationships, FALLBACK_PATRON_RELATIONSHIPS, NO_NAME_PREFIX);

    export const dependentsReadyForPickupTotal: MxSelector<number> = createSelector(
      dependents,
      (dependents) => dependents.reduce((total, dependent) => total + dependent.readyForPickupCount, 0)
    );

    export const dependentsOverdueTotal: MxSelector<number> = createSelector(
      dependents,
      (dependents) => dependents.reduce((total, dependent) => total + dependent.overdueCount, 0)
    );

    export const dependentsBalanceTotal: MxSelector<number> = createSelector(
      dependents,
      (dependents) => dependents.reduce((total, dependent) => total + dependent.accountBalance, 0)
    );
  } // namespace PatronRelationships

  export namespace RelatedPatronStateless {
    export const {
      firstNameFor,
      lastNameFor,
      patronTypeFor,
      blockCountFor,
      itemsOutCountFor,
      overdueCountFor,
      activeHoldCountFor,
      readyForPickupCountFor,
      accountBalanceFor,
      accountExpirationFor
    } = generateIndependentSelectorCreatorsFor(FALLBACK_RELATED_PATRON, NO_NAME_PREFIX);

    export const {
      fullNameFor
    } = generatePersonNameSelectorCreatorsFor(firstNameFor, alwaysEmptyStringFor, lastNameFor, alwaysEmptyStringFor, NO_NAME_PREFIX);
  } // namespace RelatedPatronStateless


  export namespace DependentDetails {
    const subState: MxSelector<PatronState['dependentDetails']> = createSelector(
      entireState,
      state => state.dependentDetails
    );

    // LOAD STATUS
    export const {
      loading,
      awaitingFirstLoad,
      loaded,
      finishedLoading
    } = generateLoadStatusSelectorsFor(subState);

    export const dependentDetails: MxSelector<RelatedPatron> = createSelector(
      subState,
      _subState => _subState.data ?? FALLBACK_RELATED_PATRON
    );

    export const {
      firstName,
      lastName,
      patronType,
      blockCount,
      accountBalance,
      accountExpiration
    } = generateSelectorsDependentOn(dependentDetails, FALLBACK_RELATED_PATRON, NO_NAME_PREFIX);

    export const {
      fullName
    } = generatePersonNameSelectorsFor(firstName, alwaysEmptyString, lastName, alwaysEmptyString, NO_NAME_PREFIX);

    export const hasBlocks: MxSelector<boolean> = createSelector(
      blockCount,
      isPositiveNumber
    );
  } // namespace DependentDetails

  export namespace DependentAccountStatus {
    const subState: MxSelector<PatronState['dependentAccountStatus']> = createSelector(
      entireState,
      state => state.dependentAccountStatus
    );

    // LOAD STATUS
    export const {
      loading,
      awaitingFirstLoad,
      loaded,
      finishedLoading
    } = generateLoadStatusSelectorsFor(subState);

    export const status: MxSelector<PatronAccountStatus> = createSelector(
      subState,
      _subState => _subState.data ?? FALLBACK_PATRON_ACCOUNT_STATUS
    );

    const generatedStatusSelectors: GeneratedSelectorsFor<PatronAccountStatus, typeof NO_NAME_PREFIX> =
      generateSelectorsDependentOn(status, FALLBACK_PATRON_ACCOUNT_STATUS, NO_NAME_PREFIX);

    export const {
      blocks,
      currentAccountBalance,
      maximumAccountBalance,
      depositBalance,
      walletBalance,
      unresolvedCheckoutCount,
      unresolvedItemRequestCount,
      underItemRequestLimit,
      monthlyItemRequestLimit
    } = generatedStatusSelectors;

    const {
      activeHoldCountsByStatus
    } = generatedStatusSelectors;

    // The API omits zero-count statuses. This normalizes the API's partial object to one with ALL statuses represented, for safety when totaling.
    const allActiveHoldCountsByStatus: MxSelector<ActiveHoldCountsByStatus> = createSelector(
      activeHoldCountsByStatus,
      counts => ({
        ...FALLBACK_ACTIVE_HOLD_COUNTS_BY_STATUS,
        ...counts
      })
    );

    export const readyForPickupHoldsCount: MxSelector<number> = createSelector(
      allActiveHoldCountsByStatus,
      counts => calculateTotalHoldCountsFor(READY_FOR_PICKUP_HOLD_STATUSES, counts)
    );

    export const onHoldHoldsCount: MxSelector<number> = createSelector(
      allActiveHoldCountsByStatus,
      counts => calculateTotalHoldCountsFor(ON_HOLD_HOLD_STATUSES, counts)
    );

    // The API also supplies this directly, but we ignore it and calculate it ourselves so that we control the meaning of "active holds".
    export const activeHoldsCount: MxSelector<number> = createSelector(
      allActiveHoldCountsByStatus,
      counts => calculateTotalHoldCountsFor(ACTIVE_HOLD_STATUSES, counts)
    );

    export const noBlocks: MxSelector<boolean> = createSelector(
      blocks,
      (_blocks) => _blocks.length === 0
    )

    export const hasBlocks: MxSelector<boolean> = createSelector(
      blocks,
      (_blocks) => isPopulatedArray(_blocks)
    )

    export const hasBalance: MxSelector<boolean> = createSelector(
      currentAccountBalance,
      (currentBalance) => currentBalance > 0
    )

    export const underMaxBalance: MxSelector<boolean> = createSelector(
      currentAccountBalance,
      maximumAccountBalance,
      (currentBalance, maximumBalance) => currentBalance < maximumBalance
    )

    export const isExpired: MxSelector<boolean> = createSelector(
      blocks,
      (_blocks) => _blocks.includes('CARD_EXPIRED')
    )
  } // namespace DependentAccountStatus
}

function findNotificationPreferenceFor(preferences: NotificationPreference[], type: PatronNotificationPreferenceType): NotificationPreference {
  if (isPopulatedArray(preferences)) {
    return preferences.find((notificationPreference) =>
      notificationPreference.notificationType === type) ?? FALLBACK_NOTIFICATION_PREFERENCE;
  }
  return FALLBACK_NOTIFICATION_PREFERENCE;
}

function notificationPreferenceToSelection(preference: NotificationPreference): ExtendedPatronNotificationPreferenceValue {
  if (preference.notifyPhone) {
    return 'Phone';
  }
  if (preference.notifyEmail) {
    return 'Email';
  }
  if (preference.notifyText) {
    return 'SMS';
  }
  return 'In-App Only';
}
